import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react"
import Api from "../../utils/Api/Api";
import { trackPaymentEvent, trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS } from '../../utils/analyticsService';

export const OrderConfirmation = () => {
    const [searchParams] = useSearchParams();
    const order_id = searchParams.get("order_id");
    const status = searchParams.get("status");
    const error_reason = searchParams.get("reason") || "unknown_error";
    
    const navigate = useNavigate();
    
    const [processingStatus, setProcessingStatus] = useState({
        message: "We're confirming your payment...",
        subMessage: "You'll be redirected automatically in a few seconds",
        isError: false,
        errorCode: null,
        gateway: null
    });
    
    // Add state for polling and timeout handling
    const [isPolling, setIsPolling] = useState(true);
    const [showRetryOptions, setShowRetryOptions] = useState(false);
    const [pollingAttempts, setPollingAttempts] = useState(0);
    const [failedPaymentDetails, setFailedPaymentDetails] = useState(null);

    // Use refs to track state without causing re-renders
    const isPollingRef = useRef(true);
    const pollingAttemptsRef = useRef(0);
    
    // Sync refs with state
    useEffect(() => {
        isPollingRef.current = isPolling;
    }, [isPolling]);
    
    useEffect(() => {
        pollingAttemptsRef.current = pollingAttempts;
    }, [pollingAttempts]);
    
    // Function to handle retry
    const handleRetryPayment = () => {
        if (order_id) {
            navigate(`/checkout?retry=true&order_id=${order_id}`);
        } else {
            navigate('/checkout');
        }
    };
    
    // Function to handle cancel
    const handleCancelPayment = () => {
        if (failedPaymentDetails?.payment_id) {
            // Call cancel payment API if we have a payment ID
            Api.CancelPayment({ payment_id: failedPaymentDetails.payment_id })
                .then(() => {
                    console.log("Payment cancelled successfully");
                    navigate('/marketplace');
                })
                .catch(err => {
                    console.error("Error cancelling payment:", err);
                    navigate('/marketplace');
                });
        } else {
            navigate('/marketplace');
        }
    };

    // Get appropriate error message based on the error code
    const getErrorMessage = (errorCode, gateway) => {
        const errorMessages = {
            'missing_order_id': 'Unable to locate your order information',
            'payment_not_successful': 'Your payment was not successful',
            'no_order_found': 'Order details not found',
            'gateway_error': `There was an issue with the ${gateway || 'payment'} gateway`,
            'timeout': 'Payment verification timed out',
            'unknown_error': 'An unexpected error occurred'
        };
        
        return errorMessages[errorCode] || 'Payment was unsuccessful';
    };

    // Process the initial parameters for failed payments
    useEffect(() => {
        if (status === "failed") {
            console.log("Failed payment detected from URL parameters");
            setProcessingStatus({
                message: "Payment failed",
                subMessage: getErrorMessage(error_reason),
                isError: true,
                errorCode: error_reason
            });
            setShowRetryOptions(true);
            setIsPolling(false);
        }
    }, [status, error_reason]);
    
    // Separate effect for handling successful payment redirects
    useEffect(() => {
        if (failedPaymentDetails?.status === 'paid' && 
            failedPaymentDetails?.is_purchase_done && 
            !isPolling && 
            processingStatus.message === "Payment successful!") {
            
            console.log("Setting up redirect for successful payment");
            const redirectTimeout = setTimeout(() => {
                navigate(`/order?order_id=${failedPaymentDetails.order_id}`);
            }, 1000);
            
            return () => {
                console.log("Cleaning up redirect timeout");
                clearTimeout(redirectTimeout);
            };
        }
    }, [failedPaymentDetails, isPolling, processingStatus.message, navigate]);
    
    // Handle payment verification with V4 API
    useEffect(() => {
        // Early exit if no order_id or already marked as failed
        if (!order_id || status === "failed") {
            return;
        }

        console.log("Starting V4 payment verification for order:", order_id);
        
        // Track page view
        trackCustomEvent(CUSTOM_EVENTS.PAYMENT_CONFIRMATION_VIEWED, {
            order_id: order_id,
            status: status,
            page_url: window.location.href,
            timestamp: new Date().toISOString(),
            source: 'web'
        }, ANALYTICS_PROVIDERS.AMPLITUDE);
        
        // Keep track of timeouts to clear them if needed
        const timeoutIds = [];
        
        // Function to poll payment status using V4 API
        const pollPaymentStatus = () => {
            if (!isPollingRef.current) {
                console.log("Polling stopped by isPolling flag");
                return;
            }
            
            const currentAttempts = pollingAttemptsRef.current;
            console.log(`V4 Payment status polling attempt ${currentAttempts + 1}`);
            
            const payload = { order_id };
            
            Api.ProcessPaymentV4(payload)
                .then(({ data: paymentResult }) => {
                    console.log("V4 Payment Result:", paymentResult);
                    
                    // Store payment details regardless of status
                    setFailedPaymentDetails(paymentResult);
                    
                    // Track payment with analytics
                    trackPaymentEvent('processing', {
                        payment_id: paymentResult.payment_id,
                        order_id: paymentResult.order_id,
                        amount: paymentResult.amount,
                        currency: paymentResult.currency || 'INR',
                        gateway: paymentResult.gateway,
                        payment_method: paymentResult.payment_method
                    });
                    
                    // Handle based on payment status
                    if (paymentResult.status === 'failed') {
                        console.log("Payment explicitly failed:", paymentResult);
                        
                        // Stop polling first
                        setIsPolling(false);
                        
                        // Update UI state together
                        setProcessingStatus({
                            message: "Payment failed",
                            subMessage: paymentResult.error 
                                ? `Error: ${paymentResult.error}` 
                                : "Your payment was unsuccessful",
                            isError: true,
                            errorCode: 'payment_not_successful',
                            gateway: paymentResult.gateway
                        });
                        setShowRetryOptions(true);
                        
                        // Track payment failed
                        trackPaymentEvent('failed', {
                            order_id: paymentResult.order_id,
                            gateway: paymentResult.gateway,
                            reason: paymentResult.error || 'unknown',
                        });
                    }
                    else if (paymentResult.status === 'paid' && paymentResult.is_purchase_done) {
                        // Track successful payment
                        trackPaymentEvent('success', {
                            value: paymentResult.amount,
                            currency: paymentResult.currency || 'INR',
                            payment_method: paymentResult.payment_method,
                            order_id: paymentResult.order_id,
                            gateway: paymentResult.gateway
                        });
                        
                        // Stop polling and update UI
                        setIsPolling(false);
                        setProcessingStatus({
                            message: "Payment successful!",
                            subMessage: "Redirecting you to your order...",
                            isError: false
                        });
                        
                        // Don't handle redirect here - the separate effect will handle it
                        console.log("Payment successful, redirect will be handled by separate effect");
                    }
                    else if (paymentResult.status === 'pending') {
                        // Increment polling attempts
                        const newAttempts = currentAttempts + 1;
                        setPollingAttempts(newAttempts);
                        pollingAttemptsRef.current = newAttempts;
                        
                        // Show "taking longer" messaging after a few attempts
                        if (newAttempts >= 3) {
                            setShowRetryOptions(true);
                            setProcessingStatus({
                                message: "Payment is taking longer than expected",
                                subMessage: "You can wait or try again",
                                isError: false
                            });
                            
                            // Continue polling at a slower rate
                            const nextPollTimeout = setTimeout(pollPaymentStatus, 5000);
                            timeoutIds.push(nextPollTimeout);
                        } else {
                            // Continue polling at normal rate
                            const nextPollTimeout = setTimeout(pollPaymentStatus, 2000);
                            timeoutIds.push(nextPollTimeout);
                        }
                    }
                    else {
                        // Handle unknown status
                        const newAttempts = currentAttempts + 1;
                        setPollingAttempts(newAttempts);
                        pollingAttemptsRef.current = newAttempts;
                        
                        // Give up after max attempts
                        if (newAttempts >= 6) {
                            setIsPolling(false);
                            setShowRetryOptions(true);
                            setProcessingStatus({
                                message: "Unable to verify payment status",
                                subMessage: "Please try again",
                                isError: true,
                                errorCode: 'timeout'
                            });
                        } else {
                            // Continue polling
                            const nextPollTimeout = setTimeout(pollPaymentStatus, 2000);
                            timeoutIds.push(nextPollTimeout);
                        }
                    }
                })
                .catch(err => {
                    console.warn("V4 Payment polling failed:", err);
                    const newAttempts = currentAttempts + 1;
                    setPollingAttempts(newAttempts);
                    pollingAttemptsRef.current = newAttempts;
                    
                    // Give up after max attempts
                    if (newAttempts >= 6) {
                        setIsPolling(false);
                        setShowRetryOptions(true);
                        setProcessingStatus({
                            message: "Payment verification failed",
                            subMessage: "Please try again",
                            isError: true,
                            errorCode: 'timeout'
                        });
                    } else {
                        // Retry with exponential backoff
                        const backoffDelay = Math.min(2000 * Math.pow(1.5, newAttempts), 8000);
                        const retryTimeout = setTimeout(pollPaymentStatus, backoffDelay);
                        timeoutIds.push(retryTimeout);
                    }
                });
        };
        
        // Start initial polling
        const initialPollTimeout = setTimeout(pollPaymentStatus, 1000);
        timeoutIds.push(initialPollTimeout);
        
        // Cleanup function
        return () => {
            console.log("Cleaning up payment verification");
            timeoutIds.forEach(id => clearTimeout(id));
        };
    }, [order_id, navigate, status]);
    
    // Handle missing order_id
    useEffect(() => {
        if (!order_id && status !== "failed") {
            console.error("Missing order_id parameter in OrderConfirmation page URL");
            setProcessingStatus({
                message: "Payment verification failed",
                subMessage: "Missing order information. Redirecting back to checkout...",
                isError: true,
                errorCode: 'missing_order_id'
            });
            
            const redirectTimeout = setTimeout(() => {
                navigate('/checkout?status=failed&reason=missing_order_id');
            }, 2000);
            
            return () => clearTimeout(redirectTimeout);
        }
    }, [order_id, navigate, status]);
    
    return (
        <>
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                color: '#FAFAFA',
                fontFamily: 'Gilroy-Medium',
                background: '#121212',
                padding: '0 20px',
                textAlign: 'center'
            }}>
                {processingStatus.isError ? (
                    // Error state with X icon instead of loader
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 74, 74, 0.1)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '20px',
                    }}>
                        <div style={{
                            fontSize: '30px',
                            color: '#ff4a4a',
                            fontWeight: 'bold',
                        }}>✕</div>
                    </div>
                ) : (
                    // Loading spinner for non-error states
                    <div className="loader" style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        borderTop: '4px solid #17CE3A',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '20px',
                        display: showRetryOptions && !isPolling ? 'none' : 'block'
                    }}></div>
                )}
                
                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                        
                        @keyframes pulse {
                            0%, 100% { opacity: 0.6; }
                            50% { opacity: 1; }
                        }
                        
                        .retry-button {
                            animation: pulse 2s infinite;
                        }
                        
                        .error-details {
                            background-color: rgba(255, 74, 74, 0.1);
                            border-radius: 8px;
                            padding: 12px;
                            margin-top: 15px;
                            font-size: 14px;
                            text-align: left;
                            max-width: 300px;
                            width: 100%;
                        }
                        
                        .error-details p {
                            margin: 5px 0;
                            opacity: 0.9;
                        }
                        
                        .error-details .label {
                            font-weight: bold;
                            opacity: 0.7;
                        }
                    `}
                </style>
                
                <h2 style={{ 
                    marginBottom: '10px',
                    color: processingStatus.isError ? '#ff4a4a' : '#FAFAFA'
                }}>
                    {processingStatus.message}
                </h2>
                
                <p style={{ marginBottom: '10px' }}>{processingStatus.subMessage}</p>
                
                {/* Show failed payment details if available */}
                {failedPaymentDetails && processingStatus.isError && (
                    <div className="error-details">
                        <p><span className="label">Payment ID:</span> {failedPaymentDetails.payment_id}</p>
                        <p><span className="label">Order ID:</span> {failedPaymentDetails.order_id}</p>
                        <p><span className="label">Gateway:</span> {failedPaymentDetails.gateway}</p>
                        {failedPaymentDetails.error && (
                            <p><span className="label">Error:</span> {failedPaymentDetails.error}</p>
                        )}
                    </div>
                )}
                
                {showRetryOptions && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        marginTop: '20px',
                        width: '100%',
                        maxWidth: '300px'
                    }}>
                        <button 
                            onClick={handleRetryPayment}
                            className="retry-button"
                            style={{
                                padding: '12px 20px',
                                background: '#17CE3A',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            TRY PAYMENT AGAIN
                        </button>
                        
                        {/* Only show Cancel Payment button if payment is not failed */}
                        {(!processingStatus.isError || failedPaymentDetails?.status === 'pending') && (
                            <button 
                                onClick={handleCancelPayment}
                                style={{
                                    padding: '12px 20px',
                                    background: 'transparent',
                                    color: 'white',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                CANCEL PAYMENT
                            </button>
                        )}
                        
                        {!processingStatus.isError && isPolling && (
                            <p style={{ marginTop: '10px', fontSize: '14px', opacity: '0.7' }}>
                                Still verifying your payment in the background...
                            </p>
                        )}
                    </div>
                )}
                
                {processingStatus.isError && !showRetryOptions && (
                    <p style={{ fontSize: '14px', opacity: '0.7', marginTop: '15px' }}>
                        If you've been charged, don't worry. Our team will resolve this issue.
                    </p>
                )}
                
                {processingStatus.isError && (
                    <p style={{ 
                        fontSize: '14px', 
                        opacity: '0.7', 
                        marginTop: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        Need help? <a 
                            href="https://wa.me/919148477518" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#17CE3A', 
                                marginLeft: '5px',
                                textDecoration: 'none' 
                            }}
                        >
                            WhatsApp Support
                        </a>
                    </p>
                )}
            </div>
        </>
    );
}